import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'left' | 'right';
  /** Accessible label for the close control. */
  closeLabel?: string;
}

/**
 * Overlay drawer. Portaled, fixed above the page, with a backdrop.
 * Opening it must not occupy layout space or reflow the rest of the UI.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = 'left',
  closeLabel = 'Close',
}: DrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bg-canvas-veil data-[state=open]:animate-fade-in fixed inset-0 z-40 backdrop-blur-sm" />
        <DialogPrimitive.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement).focus();
          }}
          className={cn(
            'fixed inset-y-0 z-50 flex w-80 max-w-[min(20rem,calc(100vw-2.5rem))] flex-col',
            'border-line bg-surface-raised shadow-lg outline-none',
            side === 'left'
              ? 'data-[state=open]:animate-drawer-in-left left-0 border-r'
              : 'data-[state=open]:animate-drawer-in-right right-0 border-l',
          )}
        >
          <div className="sheen border-line flex items-start justify-between gap-4 border-b px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="text-sm font-semibold">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-text-subtle mt-1 text-xs">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              className="text-text-faint hover:bg-surface-overlay hover:text-text -mt-1 -mr-2 rounded-sm p-2 transition-colors"
              aria-label={closeLabel}
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
          {footer ? <div className="border-line bg-surface border-t px-4 py-4">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const DrawerClose = DialogPrimitive.Close;
